import React, { useState } from 'react';
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement
} from '@stripe/react-stripe-js';
import { type StripeError } from '@stripe/stripe-js';
import type { ProductPlan, StripeProduct } from 'bundles/connect/types';
import { Alert, Button, Col, Form, Row, Spinner } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';
import { USD } from 'helpers/formatters/number_formats';
import SelectedPlan from './selected_plan';
import { profilePremiumThankYouUrl } from 'bundles/connect/helpers/prospect_api';

type Props = {
  selectedPlan: ProductPlan;
  stripeProduct: StripeProduct;
  freeTrialDays?: number
};

const STRIPE_CSS = {
  base: {
    fontSize: '16px',
    color: '#1C1D21',
    backgroundColor: '#FFFFFF',
    lineHeight: '22px'
  }
};

export default function SubscribeForm({ freeTrialDays, selectedPlan, stripeProduct }: Props) {
  const stripe = useStripe();
  const elements = useElements();
  const term = stripeProduct.annual ? 'Annually' : 'Monthly';
  const numMonths = stripeProduct.annual ? 12 : 1;
  const chargeAmount = (stripeProduct.discountedPrice || stripeProduct.price) * numMonths;

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null | StripeError | undefined>(null);
  const [signedUp, setSignedUp] = useState(false);
  const [agreeHold, setAgreeHold] = useState(freeTrialDays ? false : true);
  const [acknowledgeCharge, setAcknowledgeCharge] = useState(false);
  const [zip, setZip] = useState('');
  const [cardComplete, setCardComplete] = useState({
    number: false,
    expiry: false,
    cvc: false
  });

  const isFormValid =
    stripe &&
    !loading &&
    agreeHold &&
    acknowledgeCharge &&
    zip.trim() !== '' &&
    cardComplete.number &&
    cardComplete.expiry &&
    cardComplete.cvc &&
    !signedUp;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);
    setLoading(true);

    if (!stripe || !elements) {
      setMessage('Something went wrong.');
      setLoading(false);
      return;
    }

    const cardNumberElement = elements.getElement(CardNumberElement);
    if (!cardNumberElement) {
      setMessage('Something went wrong.');
      setLoading(false);
      return;
    }

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardNumberElement,
      billing_details: {
        address: {
          postal_code: zip
        }
      }
    });

    if (error) {
      setMessage('Something went wrong');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('/prospect/profile/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payment_method_id: paymentMethod.id,
          product_id: stripeProduct.stripeProductId
        })
      });

      const result = await response.json();

      if (result.error) {
        setMessage(result.error);
      } else {
        setSignedUp(true);
        setMessage("Thanks for signing up. Let's get you to the completion page");
        setTimeout(() => {
          window.location.href = profilePremiumThankYouUrl;
        }, 2000);
      }
    } catch (err) {
      setMessage('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Row className="align-items-start g-6">
      <Col md={4}>
        <SelectedPlan plan={selectedPlan} />
      </Col>
      <Col md={8}>
        <Form className="p-4 bg-white border rounded d-flex flex-column gap-3" onSubmit={handleSubmit}>
          {freeTrialDays && (
            <h5>Try Free for {freeTrialDays} Days</h5>
          )}
          {message && (
            <Alert className="p-3" variant={signedUp ? 'success' : 'danger'}>
              <div className="d-flex align-items-start gap-2">
                <FontAwesomeIcon className="mt-1" icon={faExclamationCircle} />
                <div className="d-flex flex-column">
                  <div className="fw-bolder">{signedUp ? 'Success' : 'An Error Occurred'}</div>
                  <div>{message}</div>
                </div>
              </div>
            </Alert>
          )}

          <Form.Group>
            <Form.Label>
              Credit Card Number <span className="text-danger">*</span>
            </Form.Label>
            <CardNumberElement
              className="border rounded px-4 py-2 stripe-form-element"
              options={{ showIcon: true, style: STRIPE_CSS }}
              onChange={e => setCardComplete(prev => ({ ...prev, number: e.complete }))}
            />
          </Form.Group>

          <Row className="g-3">
            <Col md={4}>
              <Form.Label>
                Expiration Date <span className="text-danger">*</span>
              </Form.Label>
              <CardExpiryElement
                className="border rounded px-4 py-2 stripe-form-element"
                options={{ style: STRIPE_CSS }}
                onChange={e => setCardComplete(prev => ({ ...prev, expiry: e.complete }))
                }
              />
            </Col>
            <Col md={4}>
              <Form.Label>
                Security Code <span className="text-danger">*</span>
              </Form.Label>
              <CardCvcElement
                className="border rounded px-4 py-2 stripe-form-element"
                options={{ style: STRIPE_CSS }}
                onChange={e => setCardComplete(prev => ({ ...prev, cvc: e.complete }))
                }
              />
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>
                  Zip Code <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  style={{ fontFamily: 'sans-serif' }}
                  type="text"
                  value={zip}
                  onChange={e => setZip(e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>

          <div>
            {freeTrialDays && (
              <Form.Check
                checked={agreeHold}
                className="mb-2"
                id="agree-hold-check"
                label={
                  <>
                    I agree to have a <strong>temporary hold placed on my card for {USD.format(chargeAmount, { round: true })}</strong>.
                  </>
                }
                type="checkbox"
                onChange={() => setAgreeHold(!agreeHold)}
              />
            )}

            <Form.Check
              checked={acknowledgeCharge}
              id="acknowledge-charge-check"
              label={
                <>
                  I understand that {freeTrialDays ? `if I don't cancel my subscription within ${freeTrialDays} days, ` : ''}
                  I will be charged <strong>{USD.format(chargeAmount, { round: true })}</strong> and <strong>{term}</strong> thereafter
                  until I cancel. <strong>No refunds will be processed.</strong>
                </>
              }
              type="checkbox"
              onChange={() => setAcknowledgeCharge(!acknowledgeCharge)}
            />
          </div>

          <div className="d-flex justify-content-end pt-2 border-top">
            <Button disabled={!isFormValid} type="submit" variant="secondary">
              {loading && <Spinner animation="border" size="sm"/>}
              {loading ? 'Processing' : `${freeTrialDays ? 'Start Trial' : 'Start Subscription' }` }
            </Button>
          </div>
        </Form> 
      </Col>
    </Row>
  );
}
